import React from "react";
import { Modal } from "react-bootstrap";
import styled from "styled-components";
import { CloseOutlined } from "@ant-design/icons";
import { ConfigProvider } from "antd";
import {
  lightGreyOutline,
  darkGreen,
  greyText,
  compassLightGreyBackground,
  compassYellow,
} from "../../../styles";
import { boxShadow, CompassParagraph } from "../styledComponents";
import { AIBadge } from "../../visuals/AIBadge";
import Lottie from "react-lottie-player";
import { RowContainer } from "../../containers/styledComponents";
import { ModalDescriptionBox } from "./ModalDescriptionBox";

// =============================================================================
// TYPES
// =============================================================================

import { HeroKind } from "../../../enums/Modal";

export type ModalSize = "sm" | "md" | "lg";

export type ModalType = ModalSize;

export interface HeroConfig {
  kind: HeroKind;
  source?: string;
  animation?: object;
  alt?: string;
  title?: string;
  description?: string;
  /** Content rendered after title (e.g., loading dots) */
  suffix?: React.ReactNode;
  /** Max size of the image/animation (default: 100%) */
  size?: number | string;
  /** Padding around the image/animation inside container (default: 30px) */
  padding?: number;
  /** Horizontal offset in px (positive = right, negative = left) */
  offsetX?: number;
  /** Vertical offset in px (positive = down, negative = up) */
  offsetY?: number;
  /** Whether in processing state (uses yellow accent) */
  isProcessing?: boolean;
  accentColor?: string;
  /** Border radius for the image (default: 0) */
  imageBorderRadius?: number;
  /** Opacity for the image, useful for subtle backgrounds (default: 1) */
  imageOpacity?: number;
  /** Background color of the hero container (default: compassLightGreyBackground) */
  backgroundColor?: string;
  /** Height of the hero container in pixels (default: 200) */
  height?: number;
  titleColor?: string;
}

export interface CompassModalProps {
  /** Whether the modal is visible */
  show: boolean;
  /** Handler called when modal should close */
  handleClose?: () => void;
  /** Handler called when modal should close (alias) */
  onHide?: () => void;
  /** Modal title in header */
  title?: string;
  /** Icon source for header title */
  titleIcon?: string;
  /** Size of the title icon image (default: 24) */
  titleIconSize?: number;
  /** Show AI badge next to title */
  isAIGenerated?: boolean;
  /** Custom content for header right side */
  headerRight?: React.ReactNode;
  /** Hide the close X button */
  hideCloseButton?: boolean;
  /** Show border under header */
  headerBorder?: boolean;
  /** React Bootstrap modal size (dialog width) */
  size?: "sm" | "lg" | "xl";
  /** Center modal vertically */
  centered?: boolean;
  /** Modal size preset - determines fixed height (sm: 450px, md: 600px, lg: 700px) */
  modalSize?: ModalSize;
  /** Alias for modalSize */
  modalType?: ModalSize;
  /** Custom fixed height in pixels (overrides modalSize) */
  height?: number;
  /** Custom max height in pixels */
  maxHeight?: number;
  /** Hero section - rendered at top of body, scrolls with content */
  hero?: HeroConfig;
  /** Footer content - fixed at bottom, outside scroll area */
  footer?: React.ReactNode;
  /** Modal content */
  children?: React.ReactNode;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const MODAL_HEIGHTS: Record<ModalSize, number> = {
  sm: 600,
  md: 750,
  lg: 900,
};

const HERO_IMAGE_HEIGHT = 200;
const HERO_IMAGE_PADDING = 30;

// =============================================================================
// STYLED COMPONENTS
// =============================================================================

const StyledModal = styled(Modal)<{ $height?: number; $maxHeight?: number }>`
  font-family: "Apercu", sans-serif;

  .modal-dialog {
    margin: auto;
    height: auto;
    max-height: calc(100vh - 60px);
  }

  .modal-content {
    border-radius: 14px;
    overflow: hidden;
    font-family: "Apercu", sans-serif;
    display: flex;
    flex-direction: column;
    ${({ $height, $maxHeight }): string => {
      if ($height)
        return `height: ${$height}px; max-height: calc(100vh - 60px);`;
      if ($maxHeight) return `max-height: ${$maxHeight}px;`;
      return "max-height: calc(100vh - 60px);";
    }}
  }
`;

const Header = styled.div<{ $hasBorder?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 28px;
  min-height: 42px;
  border-bottom: ${({ $hasBorder }): string =>
    $hasBorder ? "1px solid #e5e5e5" : "none"};
  background-color: white;
  flex-shrink: 0;
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  height: 42px;
`;

const TitleIconBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 14px;
  border: 1px solid ${lightGreyOutline};
  box-shadow: ${boxShadow};
  background-color: white;
`;

const TitleIconImg = styled.img<{ $iconSize?: number }>`
  width: ${({ $iconSize }): number => $iconSize ?? 24}px;
  height: ${({ $iconSize }): number => $iconSize ?? 24}px;
`;

const TitleText = styled.span`
  font-family: "Apercu", sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: ${darkGreen};
  line-height: 42px;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  height: 42px;
`;

const CloseButton = styled(CloseOutlined)`
  cursor: pointer;
  font-size: 14px;
  color: ${greyText};
  &:hover {
    color: ${darkGreen};
  }
`;

const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
`;

const BodyContent = styled.div<{ $hasHero?: boolean }>`
  padding: 0 28px;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const Footer = styled.div`
  flex-shrink: 0;
  padding: 0 28px 20px 28px;
`;

// Hero components
const HeroWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 24px 28px 16px 28px;
  flex-shrink: 0;
`;

const HeroImageContainer = styled.div<{
  $size?: number | string;
  $padding: number;
  $offsetX?: number;
  $offsetY?: number;
  $imageBorderRadius?: number;
  $imageOpacity?: number;
  $backgroundColor?: string;
  $height?: number;
}>`
  height: ${({ $height }): number => $height ?? HERO_IMAGE_HEIGHT}px;
  min-height: ${({ $height }): number => $height ?? HERO_IMAGE_HEIGHT}px;
  max-height: ${({ $height }): number => $height ?? HERO_IMAGE_HEIGHT}px;
  width: 100%;
  background: ${({ $backgroundColor }): string =>
    $backgroundColor ?? compassLightGreyBackground};
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ $padding }): number => $padding}px;
  overflow: hidden;
  box-sizing: border-box;

  img,
  svg {
    max-width: ${({ $size }): string =>
      $size ? (typeof $size === "number" ? `${$size}px` : $size) : "100%"};
    max-height: ${({ $size }): string =>
      $size ? (typeof $size === "number" ? `${$size}px` : $size) : "100%"};
    object-fit: contain;
    transform: translate(
      ${({ $offsetX }): number => $offsetX ?? 0}px,
      ${({ $offsetY }): number => $offsetY ?? 0}px
    );
    border-radius: ${({ $imageBorderRadius }): number =>
      $imageBorderRadius ?? 0}px;
    opacity: ${({ $imageOpacity }): number => $imageOpacity ?? 1};
  }

  > div {
    max-width: ${({ $size }): string =>
      $size ? (typeof $size === "number" ? `${$size}px` : $size) : "100%"};
    max-height: ${({ $size }): string =>
      $size ? (typeof $size === "number" ? `${$size}px` : $size) : "100%"};
    width: ${({ $size }): string =>
      $size ? (typeof $size === "number" ? `${$size}px` : $size) : "100%"};
    height: ${({ $size }): string =>
      $size ? (typeof $size === "number" ? `${$size}px` : $size) : "100%"};
    transform: translate(
      ${({ $offsetX }): number => $offsetX ?? 0}px,
      ${({ $offsetY }): number => $offsetY ?? 0}px
    );
  }
`;

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Standardized modal component.
 *
 * Structure:
 * - Header: title, icon, close button (fixed at top)
 * - Body: hero (optional) + children (scrollable)
 * - Footer: action buttons (fixed at bottom)
 *
 * Use `hero` prop to show an image/animation with title and description.
 * Use `modalSize` for standard sizes or `height` for custom fixed height.
 */
export const CompassModal: React.FC<CompassModalProps> = ({
  show,
  handleClose,
  onHide,
  title,
  titleIcon,
  titleIconSize,
  isAIGenerated,
  headerRight,
  hideCloseButton = false,
  headerBorder,
  size,
  centered = true,
  modalSize,
  modalType, // deprecated alias
  height,
  maxHeight,
  hero,
  footer,
  children,
}) => {
  const closeHandler = handleClose ?? onHide;
  const showHeader = title || titleIcon || headerRight || !hideCloseButton;
  const hasBorder = headerBorder ?? !!title;

  // Support both modalSize and deprecated modalType
  const sizePreset = modalSize ?? modalType;
  const effectiveHeight =
    height ?? (sizePreset ? MODAL_HEIGHTS[sizePreset] : undefined);

  return (
    <StyledModal
      show={show}
      onHide={closeHandler}
      size={size}
      centered={centered}
      $height={effectiveHeight}
      $maxHeight={maxHeight}
    >
      {showHeader && (
        <Header $hasBorder={hasBorder}>
          <HeaderTitle>
            {titleIcon && (
              <TitleIconBox>
                <TitleIconImg
                  src={titleIcon}
                  alt=""
                  $iconSize={titleIconSize}
                />
              </TitleIconBox>
            )}
            {title && <TitleText>{title}</TitleText>}
            {isAIGenerated && <AIBadge size={34} />}
          </HeaderTitle>
          <HeaderRight>
            {headerRight}
            {!hideCloseButton && <CloseButton onClick={closeHandler} />}
          </HeaderRight>
        </Header>
      )}

      <Body>
        {hero && (
          <HeroWrapper>
            <HeroImageContainer
              $size={hero.size}
              $padding={hero.padding ?? HERO_IMAGE_PADDING}
              $offsetX={hero.offsetX}
              $offsetY={hero.offsetY}
              $imageBorderRadius={hero.imageBorderRadius}
              $imageOpacity={hero.imageOpacity}
              $backgroundColor={hero.backgroundColor}
              $height={hero.height}
            >
              {hero.kind === HeroKind.IMAGE ? (
                <img src={hero.source || ""} alt={hero.alt || ""} />
              ) : (
                <Lottie loop animationData={hero.animation} play />
              )}
            </HeroImageContainer>

            {hero.title && (
              <RowContainer jc="center" mt={1} mb={1}>
                <CompassParagraph
                  fontSize={18}
                  bold={true}
                  mr={1}
                  color={hero.titleColor || darkGreen}
                >
                  {hero.title}
                </CompassParagraph>
                {hero.suffix}
              </RowContainer>
            )}

            {hero.description && (
              <ModalDescriptionBox
                content={hero.description}
                accentColor={
                  hero.accentColor ??
                  (hero.isProcessing ? compassYellow : undefined)
                }
                minHeight={50}
              />
            )}
          </HeroWrapper>
        )}

        <BodyContent $hasHero={!!hero}>
          <ConfigProvider
            getPopupContainer={(triggerNode): HTMLElement =>
              triggerNode?.parentNode as HTMLElement
            }
          >
            {children}
          </ConfigProvider>
        </BodyContent>
      </Body>

      {footer && <Footer>{footer}</Footer>}
    </StyledModal>
  );
};
